import React from "react";
import { ArrowUp, ArrowDown } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Card } from "./Card";
import { Sparkline } from "../charts/Sparkline";
import { motion, Variants } from "framer-motion";

export interface StatTileProps {
  label: string;
  value: React.ReactNode;
  icon?: LucideIcon;
  tone?: "brand" | "success" | "warning" | "error" | "neutral";
  delta?: { value: number; direction: "up" | "down" };
  deltaLabel?: string;
  trend?: number[];
  loading?: boolean;
}

const TONE_ICON_CLS: Record<NonNullable<StatTileProps["tone"]>, string> = {
  brand: "bg-brand-100 text-brand-600",
  success: "bg-success-100 text-success-600",
  warning: "bg-warning-100 text-warning-600",
  error: "bg-danger-100 text-danger-600",
  neutral: "bg-info-100 text-info-600",
};

const TONE_BAR_CLS: Record<NonNullable<StatTileProps["tone"]>, string> = {
  brand: "bg-brand-500",
  success: "bg-success-600",
  warning: "bg-warning-600",
  error: "bg-danger-600",
  neutral: "bg-info-600",
};

const TONE_SPARK_COLOR: Record<NonNullable<StatTileProps["tone"]>, string> = {
  brand: "var(--brand-500)",
  success: "var(--success-600)",
  warning: "var(--warning-600)",
  error: "var(--danger-600)",
  neutral: "var(--info-600)",
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } }
};

export const StatTile: React.FC<StatTileProps> = ({
  label,
  value,
  icon: Icon,
  tone = "brand",
  delta,
  deltaLabel,
  trend,
  loading = false,
}) => (
  <motion.div variants={itemVariants}>
    <Card className="min-w-0 relative overflow-hidden group hover:-translate-y-0.5 hover:shadow-md transition-all duration-150">
      <div className={`absolute top-0 left-0 right-0 h-1 ${TONE_BAR_CLS[tone]}`} />

      <div className="flex items-center gap-2.5 mb-4">
        {Icon && (
          <div className={`h-9 w-9 rounded-xl flex items-center justify-center flex-shrink-0 ${TONE_ICON_CLS[tone]}`}>
            <Icon className="h-4 w-4" />
          </div>
        )}
        <p className="text-xs font-medium text-ink-500 font-sans truncate">{label}</p>
      </div>

      <div className="flex items-end justify-between gap-3">
        <p className="text-2xl font-bold text-ink-900 font-sora tabular-nums leading-none">
          {loading ? "–" : value}
        </p>
        {!loading && trend && trend.length > 0 && (
          <Sparkline data={trend} color={TONE_SPARK_COLOR[tone]} width={72} height={28} />
        )}
      </div>

      {!loading && delta && (
        <div className="flex items-center gap-1.5 mt-2.5">
          {delta.value === 0 ? (
            <span className="flex items-center gap-0.5 text-[11px] font-semibold font-sans px-1.5 py-0.5 rounded-md bg-ink-100 text-ink-500">
              No change
            </span>
          ) : (
            <span
              className={`flex items-center gap-0.5 text-[11px] font-semibold font-sans px-1.5 py-0.5 rounded-md ${
                delta.direction === "up" ? "bg-success-100 text-success-600" : "bg-danger-100 text-danger-600"
              }`}
            >
              {delta.direction === "up" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
              {delta.value}%
            </span>
          )}
          {deltaLabel && <span className="text-[11px] text-ink-500 font-sans">{deltaLabel}</span>}
        </div>
      )}
    </Card>
  </motion.div>
);

export default StatTile;
